import axios from "axios";

import { buildGracePeriodSnapshotFromCompany, replaceGracePeriodSnapshot } from "../Containers/grace-period/utils";
import { getUserCredentials, normalizeCustomerType, setUserCredentials } from "../utils/common";
import { API_URL } from "../utils/Constant";
import { getViewAsPerStatusCode } from "../utils/helpers";

/** Query params for GET /useraccounts/ — only send support=true for support-login sessions. */
const getUserAccountsParams = (user_data) => (
  user_data?.uid === "support" ? { support: true } : {}
);

/**
 * Switch the current company, set local or session storage, and reload the page
 * @param {String} selectedCompany one of the companies under the current domain
 * @param {String} userTitle user's role for selectedCompany
 * @param {Object} appContext app-level state
 */
export const switchCompany = async (selectedCompany, userTitle, appContext) => {
  let user_data;
  const trimmedCompanyName = selectedCompany?.trim();
  try {
    user_data = getUserCredentials();
    setUserCredentials(null, {
      ...user_data,
      company_name: trimmedCompanyName,
      user_title: user_data.uid === "support" ? "Admin" : userTitle,
    });
  } catch (error) {
    return false;
  }

  /* Update last logged in company if not support */
  if (user_data.uid !== "support") {
    try {
      await axios.post(`${API_URL}/switchcompany/`);
    } catch (e) {
      return true;
    }
  }

  return true;
};

/**
 * Get company list (uses useraccounts API as it provides the list of all companies)
 * @returns {Object} { success, message, company_list }
 */
export const getCompanyList = async () => {
  try {
    const user_data = getUserCredentials();
    const resp = await axios.get(`${API_URL}/useraccounts/`, {
      params: getUserAccountsParams(user_data),
    });
    return {
      success: resp.data.message === "Success",
      message: resp.data.message,
      company_list: resp.data.result,
    };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Retrieves account permissions for the current logged-in user's company via useraccounts API.
 * @returns {Object} Object containing account permissions and optional customer config.
 */
export const getAccountPermissionForCompany = async () => {
  try {
    const user_data = getUserCredentials();
    const resp = await axios.get(`${API_URL}/useraccounts/`, {
      params: getUserAccountsParams(user_data),
    });

    const { result } = resp.data;
    const companyList = Array.isArray(result) ? result : [];

    if (companyList.length === 0) {
      replaceGracePeriodSnapshot(buildGracePeriodSnapshotFromCompany(null));
      return {
        accountPermissions: [],
        customerConfig: { feature_config: null, customer_type: "normal" },
      };
    }

    const trimmedCompanyName = user_data?.company_name?.trim();
    const companyData = companyList.find(
      (company) => company.company_name?.trim() === trimmedCompanyName,
    );

    if (!companyData) {
      replaceGracePeriodSnapshot(buildGracePeriodSnapshotFromCompany(null));
      return {
        accountPermissions: [],
        customerConfig: { feature_config: null, customer_type: "normal" },
      };
    }

    const accountPermissions = companyData.account_permissions || [];
    const customerConfig = {
      feature_config: companyData.feature_config || null,
      customer_type: normalizeCustomerType(companyData.customer_type),
    };

    replaceGracePeriodSnapshot(buildGracePeriodSnapshotFromCompany(companyData));

    return {
      accountPermissions, customerConfig,
    };
  } catch (e) {
    getViewAsPerStatusCode(e);
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};
